import { memo, forwardRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { designTokens } from '@design-system';

import {
  Chip,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  alpha,
} from '@mui/material';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { activeItem } from '@/store/reducers/menu';
import IconRetrieve from '../../IconRetrieve';

interface NavItemProps {
  item: {
    id: string;
    url: string;
    target?: string;
    external?: boolean;
    disabled?: boolean;
    icon?: string;
    title: string;
    chip?: {
      color: 'primary' | 'secondary';
      variant: 'filled' | 'outlined';
      size: 'small' | 'medium';
      label: string;
      avatar?: React.ReactNode;
    };
  };
  level: number;
  activeItemId: string;
  onToggle: (id: string, items: any) => void;
}

const NavItem = memo<NavItemProps>(function NavItem({
  item,
  level,
  activeItemId,
  onToggle,
}) {
  const dispatch = useAppDispatch();

  const { drawerOpen, openItem } = useAppSelector((state) => state.menu);
  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps: any = {
    component: forwardRef<HTMLAnchorElement>((props, ref) => (
      <Link ref={ref} {...props} to={item.url} target={itemTarget} />
    )),
  };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = useCallback(
    (id: any) => {
      dispatch(activeItem({ openItem: [id] }));
    },
    [dispatch]
  );

  const isSelected = activeItemId
    ? activeItemId === item.id
    : openItem.findIndex((id) => id === item.id) > -1;

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      onClick={() => {
        itemHandler(item.id);
        onToggle(item.id, item);
      }}
      // keyboard navigation of the menu (Navigation/index.tsx)
      data-nav-id={item.id}
      data-nav-kind="item"
      data-nav-level={level}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level * 10 + 10}px` : 1.25,
        pr: 1.5,
        py: level === 1 ? 1.15 : 0.9,
        minHeight: level === 1 ? 46 : 40,
        borderRadius: '9px',
        mx: 0.75,
        mb: 0.35,
        transition: `all ${designTokens.transitions.duration.normal} ${designTokens.transitions.timing.easeInOut}`,
        backgroundColor: isSelected
          ? alpha(designTokens.colors.primary[500], 0.15)
          : 'transparent',
        border: isSelected
          ? `1px solid ${alpha(designTokens.colors.primary[500], 0.35)}`
          : '1px solid transparent',
        boxShadow: isSelected
          ? `inset 3px 0 0 ${designTokens.colors.primary[500]}`
          : 'inset 3px 0 0 transparent',
        '&:hover': {
          backgroundColor: alpha(designTokens.colors.primary[500], 0.1),
          borderColor: alpha(designTokens.colors.primary[500], 0.2),
        },
        // keyboard focus must be plain to see (menu is fully keyboard-driven)
        '&.Mui-focusVisible, &:focus-visible': {
          backgroundColor: alpha(designTokens.colors.primary[500], 0.18),
          outline: `2px solid ${designTokens.colors.primary[400]}`,
          outlineOffset: '-2px',
        },
        '&.Mui-selected': {
          backgroundColor: alpha(designTokens.colors.primary[500], 0.15),
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.2),
          },
        },
        ...(!drawerOpen && {
          borderRadius: '12px',
          border: '1px solid transparent',
          boxShadow: 'none',
          '&:hover': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.15),
          },
          '&.Mui-selected': {
            backgroundColor: alpha(designTokens.colors.primary[500], 0.2),
            '&:hover': {
              backgroundColor: alpha(designTokens.colors.primary[500], 0.25),
            },
          },
        }),
      }}
    >
      <ListItemIcon
        sx={{
          minWidth: level === 1 ? 34 : 26,
          color: isSelected
            ? designTokens.colors.primary[500]
            : designTokens.colors.neutral[400],
          transition: `color ${designTokens.transitions.duration.fast}`,
          ...(!drawerOpen && {
            minWidth: 0,
            mr: 0,
            justifyContent: 'center',
          }),
        }}
      >
        {level === 1 ? (
          <IconRetrieve
            icon={item.icon || 'Icon' + item.title.replace(/\s+|\.*$/g, '')}
            open={isSelected}
          />
        ) : (
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              backgroundColor: isSelected
                ? designTokens.colors.primary[500]
                : designTokens.colors.neutral[500],
              transition: `all ${designTokens.transitions.duration.fast}`,
            }}
          />
        )}
      </ListItemIcon>

      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography
              variant={level === 1 ? 'body2' : 'caption'}
              sx={{
                fontFamily: 'inherit',
                fontWeight: isSelected ? 600 : 500,
                color: isSelected
                  ? designTokens.colors.neutral[50]
                  : designTokens.colors.neutral[300],
                letterSpacing: '0.01em',
                transition: `all ${designTokens.transitions.duration.fast}`,
              }}
            >
              {item.title}
            </Typography>
          }
        />
      )}

      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          sx={{
            height: 20,
            fontWeight: 600,
            borderRadius: '6px',
          }}
        />
      )}
    </ListItemButton>
  );
});

NavItem.displayName = 'NavItem';

export default NavItem;
